import { createClient } from '@supabase/supabase-js';
import { safeStorageAdapter } from './safeStorage';
import { authLock } from './authLock';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not configured. Using fallback mode.');
}

export const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: safeStorageAdapter,
        lock: authLock,
      },
    })
    : null;

// Helper to check if Supabase is configured
export const isSupabaseConfigured = () => {
  return Boolean(supabaseUrl && supabaseAnonKey);
};

// Auth helpers
export const signUp = async (email, password, metadata = {}) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: metadata }
  });
  if (error) throw error;
  return data;
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const getUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

// Database helpers with org_id filtering
export const db = {
  // Organizations
  organizations: {
    get: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    getBySlug: async (slug) => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('slug', slug)
        .single();
      if (error) throw error;
      return data;
    }
  },

  // Services
  services: {
    list: async (orgId) => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    listActive: async (orgId) => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('org_id', orgId)
        .eq('active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
    create: async (service) => {
      const { data, error } = await supabase
        .from('services')
        .insert(service)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);
      if (error) throw error;
    }
  },

  // Staff
  staff: {
    list: async (orgId) => {
      const { data, error } = await supabase
        .from('staff')
        .select('*, staff_services(service_id)')
        .eq('org_id', orgId)
        .order('first_name');
      if (error) throw error;
      return data.map(s => ({
        ...s,
        service_ids: s.staff_services.map(ss => ss.service_id) || []
      }));
    },
    listActive: async (orgId) => {
      const { data, error } = await supabase
        .from('staff')
        .select('*, staff_services(service_id)')
        .eq('org_id', orgId)
        .eq('active', true)
        .order('first_name');
      if (error) throw error;
      return data.map(s => ({
        ...s,
        service_ids: s.staff_services.map(ss => ss.service_id) || []
      }));
    },
    create: async (staffData) => {
      const { service_ids, ...staff } = staffData;
      const { data, error } = await supabase
        .from('staff')
        .insert(staff)
        .select()
        .single();
      if (error) throw error;

      // Add service mappings
      if (service_ids.length > 0) {
        await supabase
          .from('staff_services')
          .insert(service_ids.map(sid => ({ staff_id: data.id, service_id: sid })));
      }

      return { ...data, service_ids };
    },
    update: async (id, staffData) => {
      const { service_ids, ...updates } = staffData;
      const { data, error } = await supabase
        .from('staff')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // Update service mappings
      if (service_ids !== undefined) {
        await supabase.from('staff_services').delete().eq('staff_id', id);
        if (service_ids.length > 0) {
          await supabase
            .from('staff_services')
            .insert(service_ids.map(sid => ({ staff_id: id, service_id: sid })));
        }
      }

      return { ...data, service_ids };
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('staff')
        .delete()
        .eq('id', id);
      if (error) throw error;
    }
  },

  // Clients
  clients: {
    list: async (orgId, search = null) => {
      let query = supabase
        .from('clients')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (search) {
        query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    get: async (id) => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },
    create: async (client) => {
      const { data, error } = await supabase
        .from('clients')
        .insert(client)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    findByEmail: async (orgId, email) => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('org_id', orgId)
        .eq('email', email)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  },

  // Appointments
  appointments: {
    list: async (orgId, filters = {}) => {
      let query = supabase
        .from('appointments')
        .select(`
          *,
          services(name, color, duration),
          staff(first_name, last_name, color),
          clients(first_name, last_name, email, phone)
        `)
        .eq('org_id', orgId)
        .order('start_time');

      if (filters.start_date) {
        query = query.gte('start_time', filters.start_date);
      }
      if (filters.end_date) {
        query = query.lte('start_time', filters.end_date);
      }
      if (filters.staff_id) {
        query = query.eq('staff_id', filters.staff_id);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data.map(apt => ({
        ...apt,
        service_name: apt.services.name,
        service_color: apt.services.color,
        staff_name: apt.staff ? `${apt.staff.first_name} ${apt.staff.last_name}` : null,
        staff_color: apt.staff.color,
        client_name: apt.clients ? `${apt.clients.first_name} ${apt.clients.last_name}` :
          (apt.client_first_name && apt.client_last_name ? `${apt.client_first_name} ${apt.client_last_name}` : null)
      }));
    },
    create: async (appointment) => {
      const { data, error } = await supabase
        .from('appointments')
        .insert(appointment)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('appointments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    checkConflict: async (orgId, staffId, startTime, endTime, excludeId = null) => {
      let query = supabase
        .from('appointments')
        .select('id')
        .eq('org_id', orgId)
        .eq('staff_id', staffId)
        .not('status', 'in', '(cancelled,expired)')
        .or(`and(start_time.lt.${endTime},end_time.gt.${startTime})`);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.length > 0;
    }
  },

  // Payments
  payments: {
    list: async (orgId) => {
      const { data, error } = await supabase
        .from('payments')
        .select('*, appointments(*)')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    create: async (payment) => {
      const { data, error } = await supabase
        .from('payments')
        .insert(payment)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    updateBySessionId: async (sessionId, updates) => {
      const { data, error } = await supabase
        .from('payments')
        .update(updates)
        .eq('stripe_checkout_session_id', sessionId)
        .select()
        .single();
      if (error) throw error;
      return data;
    }
  },

  // Stripe Events (Idempotency)
  stripeEvents: {
    exists: async (eventId) => {
      const { data, error } = await supabase
        .from('stripe_events')
        .select('stripe_event_id')
        .eq('stripe_event_id', eventId)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    },
    create: async (eventId, type, payload) => {
      const { data, error } = await supabase
        .from('stripe_events')
        .insert({ stripe_event_id: eventId, type, payload })
        .select()
        .single();
      if (error) throw error;
      return data;
    }
  },

  // Enterprise Leads
  enterpriseLeads: {
    create: async (lead) => {
      const { data, error } = await supabase
        .from('enterprise_leads')
        .insert(lead)
        .select()
        .single();
      if (error) throw error;
      return data;
    }
  },

  // Events
  events: {
    create: async (event) => {
      const { data, error } = await supabase
        .from('events')
        .insert(event)
        .select()
        .single();
      if (error) throw error;
      return data;
    }
  },

  // Availability
  availability: {
    get: async (staffId) => {
      const { data, error } = await supabase
        .from('availability_rules')
        .select('*')
        .eq('staff_id', staffId)
        .order('day_of_week');
      if (error) throw error;
      return data;
    },
    set: async (orgId, staffId, rules) => {
      // Delete existing rules
      await supabase
        .from('availability_rules')
        .delete()
        .eq('staff_id', staffId);

      // Insert new rules
      if (rules.length > 0) {
        const { data, error } = await supabase
          .from('availability_rules')
          .insert(rules.map(r => ({ ...r, org_id: orgId, staff_id: staffId })))
          .select();
        if (error) throw error;
        return data;
      }
      return [];
    }
  },

  // Absences
  absences: {
    list: async (orgId, staffId = null) => {
      let query = supabase
        .from('absences')
        .select('*')
        .eq('org_id', orgId);

      if (staffId) {
        query = query.eq('staff_id', staffId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    create: async (absence) => {
      const { data, error } = await supabase
        .from('absences')
        .insert(absence)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('absences')
        .delete()
        .eq('id', id);
      if (error) throw error;
    }
  },

  // Waitlist
  waitlist: {
    list: async (orgId) => {
      const { data, error } = await supabase
        .from('waitlist')
        .select('*, services(name), staff(first_name, last_name)')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    create: async (entry) => {
      const { data, error } = await supabase
        .from('waitlist')
        .insert(entry)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('waitlist')
        .delete()
        .eq('id', id);
      if (error) throw error;
    }
  }
};

export default supabase;
